import React from 'react';
import {
    Avatar,
    Box,
    Chip,
    IconButton,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ArrowBackIosNewRounded,
    CalendarTodayRounded,
    PhoneIphoneRounded,
} from "@mui/icons-material";
import { encodeId, toPriceWithComma } from "../../../helper/utils";
import { useNavigate } from "react-router-dom";

function getNearestExpirationDate(allCredit) {
    try {
        const activeCredits = allCredit?.filter(c => c.Status === "ACTIVE" && c.ExpireDate);
        if (!activeCredits || activeCredits.length === 0) return null;
        let nearest = activeCredits[0]?.ExpireDate;
        for (const item of activeCredits) {
            if (new Date(item?.ExpireDate) < new Date(nearest)) {
                nearest = item?.ExpireDate;
            }
        }
        return nearest ? new Date(nearest).toLocaleDateString('fa-IR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        }) : null;
    } catch (e) {
        return null;
    }
}

function getInitials(name) {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
        return parts[0][0] + (parts[1][0] || "");
    }
    return name.slice(0, 2);
}

const _UserTableRow = ({ row }) => {
    const navigate = useNavigate();
    const nearestExp = getNearestExpirationDate(row?.CreditList);
    const hasCredit = (row?.TotalCredit || 0) > 0;
    const userPhone = row?.User?.PhoneNumber || row?.PhoneNumber;

    return (
        <TableRow
            hover
            onClick={() => navigate("/personnel/detail/" + encodeId(row.Id))}
            sx={{
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                '&:last-child td, &:last-child th': { border: 0 },
                '&:hover': {
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(28, 139, 156, 0.04)'
                }
            }}
        >
            {/* Avatar */}
            <TableCell>
                <Avatar
                    src={row?.User?.Avatar?.Url}
                    alt={row?.User?.FullName}
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2.5,
                        bgcolor: 'primary.main',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        border: '1.5px solid',
                        borderColor: 'background.paper'
                    }}
                >
                    {getInitials(row?.User?.FullName)}
                </Avatar>
            </TableCell>

            {/* Full Name & Phone */}
            <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.3 }}>
                    {row?.User?.FullName || "بدون نام"}
                </Typography>
                {userPhone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                        <PhoneIphoneRounded sx={{ fontSize: 13, opacity: 0.7 }} />
                        <Typography variant="caption" sx={{ dir: 'ltr', letterSpacing: 0.5 }}>
                            {userPhone}
                        </Typography>
                    </Box>
                )}
            </TableCell>

            {/* Groups */}
            <TableCell>
                {(() => {
                    const groupsList = (row?.PersonnelGroups && row.PersonnelGroups.length > 0)
                        ? row.PersonnelGroups
                        : (row?.PersonnelGroup ? [row.PersonnelGroup] : []);

                    if (groupsList.length === 0) {
                        return (
                            <Chip
                                size="small"
                                label="بدون گروه"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.72rem',
                                    borderRadius: 1.8,
                                    bgcolor: 'action.hover',
                                    color: 'text.secondary',
                                }}
                            />
                        );
                    }

                    if (groupsList.length === 1) {
                        return (
                            <Chip
                                size="small"
                                label={groupsList[0].Name}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    borderRadius: 1.8,
                                    bgcolor: 'rgba(45, 107, 226, 0.08)',
                                    color: 'primary.main',
                                    border: '1px solid',
                                    borderColor: 'rgba(45, 107, 226, 0.2)',
                                }}
                            />
                        );
                    }

                    const visibleGroups = groupsList.slice(0, 2);
                    const remainingCount = groupsList.length - 2;
                    const allNames = groupsList.map(g => g.Name).join('، ');

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            {visibleGroups.map(g => (
                                <Chip
                                    key={g.Id}
                                    size="small"
                                    label={g.Name}
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.72rem',
                                        borderRadius: 1.8,
                                        bgcolor: 'rgba(45, 107, 226, 0.08)',
                                        color: 'primary.main',
                                        border: '1px solid',
                                        borderColor: 'rgba(45, 107, 226, 0.2)',
                                    }}
                                />
                            ))}
                            {remainingCount > 0 && (
                                <Tooltip title={allNames} arrow>
                                    <Chip
                                        size="small"
                                        label={`+${remainingCount}`}
                                        variant="outlined"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '0.7rem',
                                            borderRadius: 1.8,
                                            height: 22,
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    );
                })()}
            </TableCell>

            {/* Total Credit */}
            <TableCell>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 800,
                        color: hasCredit ? 'success.main' : 'text.disabled',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    {toPriceWithComma(row?.TotalCredit)}
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        تومان
                    </Typography>
                </Typography>
            </TableCell>

            {/* Active Credits Count */}
            <TableCell>
                <Chip
                    size="small"
                    label={`${row?.CreditList?.length || 0} اعتبار`}
                    variant="outlined"
                    sx={{
                        borderRadius: 1.5,
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        height: 24,
                        borderColor: (row?.CreditList?.length || 0) > 0 ? 'divider' : 'transparent'
                    }}
                />
            </TableCell>

            {/* Nearest Expiration */}
            <TableCell>
                {nearestExp ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <CalendarTodayRounded sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {nearestExp}
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        ندارد
                    </Typography>
                )}
            </TableCell>

            {/* Birthday */}
            <TableCell>
                <Typography variant="body2" sx={{ color: row?.User?.Birthday ? 'text.secondary' : 'text.disabled', fontSize: '0.82rem' }}>
                    {row?.User?.Birthday ? new Date(row?.User?.Birthday).toLocaleDateString('fa-IR', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                    }) : "—"}
                </Typography>
            </TableCell>

            {/* Action Arrow */}
            <TableCell align="center">
                <Tooltip title="مشاهده پرونده کارمند">
                    <IconButton
                        size="small"
                        sx={{
                            bgcolor: 'action.hover',
                            color: 'text.secondary',
                            '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'white'
                            }
                        }}
                    >
                        <ArrowBackIosNewRounded sx={{ fontSize: 13 }} />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};

export default _UserTableRow;
